import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult } from "@tanstack/react-query";
import { keyWithPrincipal } from "@/queries/principal-key";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import {
    CreateWarmPoolRequest,
    DeleteWarmPoolRequest,
    ListWarmPoolsRequest,
    ListWarmPoolsRequest_Filter,
    UpdateWarmPoolRequest,
    WarmPoolPhase,
    type WarmPool,
} from "gitpod-next-api/gitpod/v1/prebuild_pb";
import type { WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";

export const WARM_POOL_MIN_SIZE = 1;
export const WARM_POOL_MAX_SIZE = 20;
export const WARM_POOL_DEFAULT_SIZE = 1;

export type WarmPoolLocalEntry = {
    enabled: boolean;
    size: number;
    mutating: boolean;
};

/**
 * Grouped warm pool props passed through EnvironmentClassItem and
 * EnvironmentClassOrderedList to avoid prop explosion.
 */
export type WarmPoolConfig = {
    warmPoolsByEnvClass?: Map<string, PlainWarmPool>;
    warmPoolLocalState: Map<string, WarmPoolLocalEntry>;
    onToggle: (envClassId: string, enabled: boolean) => void;
    onSizeChange: (envClassId: string, size: number) => Promise<void>;
    onLocalSizeChange: (envClassId: string, size: number) => void;
};

/**
 * Returns true if the warm pool is marked for deletion or already deleted.
 * Checks both spec.desiredPhase (API intent) and status.phase (runner state)
 * because the runner may not have processed the deletion yet.
 */
export function isWarmPoolDeleting(pool: PlainWarmPool): boolean {
    const statusPhase = pool.status?.phase;
    const desiredPhase = pool.spec?.desiredPhase;
    return (
        statusPhase === WarmPoolPhase.DELETING ||
        statusPhase === WarmPoolPhase.DELETED ||
        desiredPhase === WarmPoolPhase.DELETING ||
        desiredPhase === WarmPoolPhase.DELETED
    );
}

export const warmPoolQueryKeys = {
    listForProject: (projectId?: string) => keyWithPrincipal(["warmPools", "list", { projectId }]),
};

export type PlainWarmPool = PlainMessage<WarmPool>;

const toPlainWarmPool = (warmPool: WarmPool): PlainWarmPool => toPlainMessage(warmPool);

/**
 * Lists warm pools for a project. Returns a map of environmentClassId -> WarmPool
 * for easy lookup when rendering per-class warm pool state.
 */
export const useProjectWarmPools = (projectId?: string, enabled = true) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: warmPoolQueryKeys.listForProject(projectId),
        queryFn: async () => {
            if (!projectId) {
                return new Map<string, PlainWarmPool>();
            }

            const { warmPools } = await api.prebuildService.listWarmPools(
                new ListWarmPoolsRequest({
                    filter: new ListWarmPoolsRequest_Filter({
                        projectIds: [projectId],
                    }),
                }),
            );

            const byEnvClass = new Map<string, PlainWarmPool>();
            for (const pool of warmPools) {
                const envClassId = pool.metadata?.environmentClassId;
                if (envClassId) {
                    byEnvClass.set(envClassId, toPlainWarmPool(pool));
                }
            }
            return byEnvClass;
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!projectId && enabled,
        staleTime: 1_000 * 30,
        gcTime: 1_000 * 60 * 5,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
    });
};

export type CreateWarmPoolParams = {
    projectId: string;
    environmentClassId: string;
    desiredSize: number;
};

export const useCreateWarmPool = (): UseMutationResult<PlainWarmPool, Error, CreateWarmPoolParams> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateWarmPoolParams) => {
            const { warmPool } = await api.prebuildService.createWarmPool(
                new CreateWarmPoolRequest({
                    projectId: params.projectId,
                    environmentClassId: params.environmentClassId,
                    desiredSize: params.desiredSize,
                }),
            );
            if (!warmPool) {
                throw new Error("Failed to create warm pool");
            }
            return toPlainWarmPool(warmPool);
        },
        onSuccess: (_created, variables) => {
            void queryClient.invalidateQueries({
                queryKey: warmPoolQueryKeys.listForProject(variables.projectId),
            });
        },
    });
};

export type UpdateWarmPoolParams = {
    warmPoolId: string;
    projectId: string;
    desiredSize: number;
};

export const useUpdateWarmPool = (): UseMutationResult<PlainWarmPool, Error, UpdateWarmPoolParams> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateWarmPoolParams) => {
            const { warmPool } = await api.prebuildService.updateWarmPool(
                new UpdateWarmPoolRequest({
                    warmPoolId: params.warmPoolId,
                    desiredSize: params.desiredSize,
                }),
            );
            if (!warmPool) {
                throw new Error("Failed to update warm pool");
            }
            return toPlainWarmPool(warmPool);
        },
        onSuccess: (_updated, variables) => {
            void queryClient.invalidateQueries({
                queryKey: warmPoolQueryKeys.listForProject(variables.projectId),
            });
        },
    });
};

export type DeleteWarmPoolParams = {
    warmPoolId: string;
    projectId: string;
};

export const useDeleteWarmPool = (): UseMutationResult<void, Error, DeleteWarmPoolParams> => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: DeleteWarmPoolParams) => {
            await api.prebuildService.deleteWarmPool(
                new DeleteWarmPoolRequest({
                    warmPoolId: params.warmPoolId,
                }),
            );
        },
        onSuccess: (_result, variables) => {
            void queryClient.invalidateQueries({
                queryKey: warmPoolQueryKeys.listForProject(variables.projectId),
            });
        },
    });
};

/**
 * Handles warm pool events from the WatchEvents stream.
 * Invalidates all warm pool list queries so the UI picks up
 * create/update/delete changes pushed by the server.
 */
export const handleWarmPoolEvent = async (client: QueryClient, _evt: WatchEventsResponse) => {
    await client.invalidateQueries({ queryKey: keyWithPrincipal(["warmPools", "list"]) });
};
